import React, { useState } from 'react';
import { CalendarPlusIcon, CheckIcon } from 'lucide-react';
import { CALENDAR_NOTE, PILLARS_NOTE } from '../../data/stampDuty';
import { formatCurrency, formatDate } from '../../utils/roadmapCalcs';
import { buildCheckInCalendar, downloadCalendar } from '../../utils/calendarInvite';
import { calculatePillars } from '../../utils/savingsPillars';
import type { RoadmapAnswers, RoadmapResults } from '../../types/roadmap';
interface SavingsPillarsProps {
  answers: RoadmapAnswers;
  results: RoadmapResults;
}

/**
 * Three staged milestones across what is left to save, on a 1 : 2 : 2 split.
 *
 * The first milestone carries the visual weight even though its segment is the
 * smallest — the emphasis lives in the focal line above the bar, so the bar can
 * stay proportional and truthful about the split while the easiest, nearest win
 * is still what the eye lands on.
 */
export function SavingsPillars({
  answers,
  results
}: SavingsPillarsProps) {
  const [added, setAdded] = useState(false);
  const pillars = calculatePillars(results.savingsGap, answers.couldSavePerPay, answers.payFrequency);
  if (pillars.length === 0) return null;
  const first = pillars[0];
  const hasDates = first.date !== null;
  const addToCalendar = () => {
    const ics = buildCheckInCalendar(pillars, answers.couldSavePerPay, answers.payFrequency);
    if (!ics) return;
    downloadCalendar(ics, 'savings-check-ins.ics');
    setAdded(true);
  };
  return <section className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm dark:shadow-none p-6 sm:p-8" data-id="element-419" data-mp="2o2lmj4zhnrj8">
      <h2 className="text-xl font-bold text-slate-900 dark:text-white" data-id="element-420" data-mp="2j4k14delbg4l">
        Your three savings pillars
      </h2>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed" data-id="element-421" data-mp="2f4jh8d8q2cq5">
        {PILLARS_NOTE}
      </p>

      {/* Pillar one — the focal point. Nearest, smallest, most actionable. */}
      <div className="mt-7" data-id="element-422" data-mp="d8zck5l0uih6">
        <p className="text-sm font-medium text-primary-600 dark:text-primary-400" data-id="element-423" data-mp="26u2h5kt0pq0s">
          Your first milestone
        </p>
        <p className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500 tabular-nums" data-id="element-424" data-mp="2blkjodaovynv">
          {formatCurrency(first.cumulative)}
        </p>
        {hasDates && <p className="mt-1 text-base text-slate-600 dark:text-slate-300" data-id="element-425" data-mp="2pkaj3zkzeafe">
            by {formatDate(first.date as Date)}
          </p>}
      </div>

      {/* Proportional bar. Segment widths are honest to the 1 : 2 : 2 split;
          only the first is filled, so the largest stage never dominates. */}
      <div className="mt-6 flex gap-1 h-3" aria-hidden="true" data-id="element-426" data-mp="2liglk0631mjg">
        {pillars.map((pillar, position) => <div key={pillar.index} style={{
        flexGrow: pillar.share
      }} className={`rounded-full ${position === 0 ? 'bg-gradient-to-r from-accent-500 to-primary-500' : 'bg-slate-200 dark:bg-white/10'}`} data-id="element-427" data-mp="153xdu1bawovw" data-mpi={position} />)}
      </div>

      <ol className="mt-4 space-y-2" data-id="element-428" data-mp="xn0lmj0qawiu">
        {pillars.map((pillar, position) => <li key={pillar.index} className="flex items-baseline justify-between gap-4" data-id="element-429" data-mp="1bjxmzeeh3wdz" data-mpi={position}>
            <span className={`text-sm ${position === 0 ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`} data-id="element-430" data-mp="16jt1tb2medhf">
              Pillar {pillar.index} · save {formatCurrency(pillar.amount)}
            </span>
            <span className={`text-sm tabular-nums whitespace-nowrap ${position === 0 ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'}`} data-id="element-431" data-mp="nuuu1aee2h46">
              {pillar.date ? formatDate(pillar.date) : 'No date yet'}
            </span>
          </li>)}
      </ol>

      <p className="mt-4 text-xs text-slate-400 dark:text-slate-500 leading-relaxed" data-id="element-432" data-mp="2zirnn79k43q9">
        These are the amounts still to save, so they add up to your{' '}
        {formatCurrency(results.savingsGap)} gap rather than the full savings
        goal. The first is deliberately the smallest.
      </p>

      {hasDates ? <>
          <button type="button" onClick={addToCalendar} className="mt-6 inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl border border-primary-500 text-base font-semibold text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors" data-id="element-433" data-mp="3dapokut46tbc">
            {added ? <CheckIcon className="w-5 h-5" data-id="element-434" data-mp="1sw5sojlfm63j" /> : <CalendarPlusIcon className="w-5 h-5" data-id="element-435" data-mp="1wwfz1xd4yyix" />}
            {added ? 'Check-ins downloaded — open the file to add them' : 'Add my check-ins to my calendar'}
          </button>
          <p className="mt-3 text-xs text-slate-400 dark:text-slate-500 leading-relaxed" data-id="element-436" data-mp="1km8bba37cbt9">
            {CALENDAR_NOTE}
          </p>
        </> : <p className="mt-6 text-sm text-slate-500 dark:text-slate-400 leading-relaxed" data-id="element-437" data-mp="1fl0zmzxxushb">
          Add an amount you could save each pay to get dates for these
          milestones and a set of calendar reminders.
        </p>}
    </section>;
}